import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Button,
    Form,
    Input,
    DatePicker,
    ConfigProvider,
    Checkbox,
    Radio,
    Tooltip,
    Table,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getLeadershipManagement, createOrUpdateLeadershipManagement } from '../../core/center-accreditation/centerAccreditationApi';

import './ScLeadershipManagement.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

// 8.4 Qualification improvement matrix frequencies
const qualificationFrequencies = [
    { code: 'dazniau_kartą_per_pusmetį', title: '8.4.1 Dažniau kaip kartą per pusmetį' },
    { code: 'kartą_per_metus', title: '8.4.2 Kartą per metus' },
    { code: 'rečiau_nei_kartą_per_metus', title: '8.4.3 Rečiau nei kartą per metus' },
];

// 8.5 Strategic decision making
const strategicDecisionMakingOptions = [
    { code: 'vadovas_priima', title: '8.5.1 sprendimus priima institucijos vadovas' },
    { code: 'vadybininkų_grupė', title: '8.5.2 sprendimai priimami institucijos vadybininkų grupėje' },
    { code: 'dalyvaujant_visiems', title: '8.5.3 dalyvaujant visiems institucijos darbuotojams' },
    { code: 'aukštesnės_institucijos', title: '8.5.4 strateginius sprendimus dažniausiai priima aukštesnės institucijos' },
];

// 8.6 Effective work form
const effectiveWorkFormOptions = [
    { code: 'individualus', title: '8.6.1 individualus darbas' },
    { code: 'grupėmis_komandinis', title: '8.6.2 darbas grupėmis/komandinis darbas' },
    { code: 'vienodai_efektyvios', title: '8.6.3 vienodai efektyvios abi darbo formos' },
];

// 8.8 Qualification improvement initiative
const qualificationImprovementInitiativeOptions = [
    { code: 'savo_asmeninė', title: '8.8.1 savo asmeninė' },
    { code: 'institucijos_vadovų', title: '8.8.2 institucijos vadovų' },
];

// 8.10 Qualification improvement encouragement
const qualificationImprovementEncouragementOptions = [
    { code: 'skatinamas_materialiai', title: '8.10.1 skatinamas materialiai' },
    { code: 'sudarant_sąlygas_mokytis', title: '8.10.2 sudarant sąlygas mokytis' },
    { code: 'skatinant_morališkai', title: '8.10.3 skatinant morališkai' },
    { code: 'kita', title: '8.10.4 kita (įrašykite)' },
];

// 8.11 Competence portfolios
const competencePortfoliosOptions = [
    { code: 'vadovas', title: '8.11.1 vadovas' },
    { code: 'darbuotojai', title: '8.11.2 darbuotojai' },
];

// 8.12 Image building activities
const imageBuildingActivitiesOptions = [
    { code: 'projektų_renginiai', title: '8.12.1 projektų pradžios ir pabaigos renginiai' },
    { code: 'tradicinės_šventės', title: '8.12.2 tradicinės šventės' },
    { code: 'tradiciniai_vizitai', title: '8.12.3 tradiciniai vizitai pas klientus' },
    { code: 'interneto_svetainė', title: '8.12.4 institucija savo interneto svetainę laiko svarbiu įvaizdžio formavimo veiksniu' },
    { code: 'funkcinis_estetinis', title: '8.12.5 rimtai rūpinasi centro funkciniu-estetiniu vaizdu' },
    { code: 'bukletai_lankstinukai', title: '8.12.6 rengia savo organizacijos ir jos siūlomų paslaugų bukletus, lankstinukus ir kitą medžiagą' },
    { code: 'kita', title: '8.12.7 kita (įrašykite)' },
];

const ScLeadershipManagement: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { leadershipManagement, isLeadershipManagementFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [qualificationImprovementEncouragementOther, setQualificationImprovementEncouragementOther] = useState<string>('');
    const [imageBuildingActivitiesOther, setImageBuildingActivitiesOther] = useState<string>('');

    useEffect(() => {
        dispatch(getLeadershipManagement({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    useEffect(() => {
        if (leadershipManagement) {
            // Initialize qualification improvement matrix
            // Helper function to convert string "true"/"false" to boolean
            const stringToBoolean = (value: any): boolean => {
                if (typeof value === 'boolean') return value;
                if (typeof value === 'string') return value.toLowerCase() === 'true';
                return false;
            };

            // Map database matrix to match qualificationFrequencies order
            const dbMatrix = leadershipManagement.qualification_improvement_matrix || [];
            const matrix = qualificationFrequencies.map(freq => {
                // Find matching item from database by frequency code
                const dbItem = dbMatrix.find((item: any) => item.frequency === freq.code);

                if (dbItem) {
                    // Convert string booleans to actual booleans
                    return {
                        frequency: freq.code,
                        management: stringToBoolean(dbItem.management),
                        pedagogical_psychological: stringToBoolean(dbItem.pedagogical_psychological),
                        andragogical: stringToBoolean(dbItem.andragogical),
                        other: stringToBoolean(dbItem.other),
                        other_text: dbItem.other_text || '',
                    };
                }
                // Default values if frequency not found in database
                return {
                    frequency: freq.code,
                    management: false,
                    pedagogical_psychological: false,
                    andragogical: false,
                    other: false,
                    other_text: '',
                };
            });

            form.setFieldsValue({
                head_education_level: leadershipManagement.head_education_level || '',
                has_head_management_education: leadershipManagement.has_head_management_education || false,
                head_speciality: leadershipManagement.head_speciality || '',
                has_management_experience: leadershipManagement.has_management_experience || false,
                management_experience_description: leadershipManagement.management_experience_description || '',
                qualification_improvement_matrix: matrix,
                strategic_decision_making: leadershipManagement.strategic_decision_making || [],
                effective_work_form: leadershipManagement.effective_work_form || null,
                staff_training_needs_researched: leadershipManagement.staff_training_needs_researched || false,
                qualification_improvement_initiative: leadershipManagement.qualification_improvement_initiative || [],
                qualification_improvement_planned: leadershipManagement.qualification_improvement_planned || false,
                qualification_improvement_encouragement: leadershipManagement.qualification_improvement_encouragement || [],
                competence_portfolios_prepared: leadershipManagement.competence_portfolios_prepared || [],
                image_building_activities: leadershipManagement.image_building_activities || [],
                specialists_invited_for_image: leadershipManagement.specialists_invited_for_image || false,
                specialists_invited_description: leadershipManagement.specialists_invited_description || '',
            });

            // Set "other" text fields
            setQualificationImprovementEncouragementOther(leadershipManagement.qualification_improvement_encouragement_other || '');
            setImageBuildingActivitiesOther(leadershipManagement.image_building_activities_other || '');
        } else {
            // Initialize with default values
            form.setFieldsValue({
                head_education_level: '',
                has_head_management_education: false,
                head_speciality: '',
                has_management_experience: false,
                management_experience_description: '',
                qualification_improvement_matrix: qualificationFrequencies.map(freq => ({
                    frequency: freq.code,
                    management: false,
                    pedagogical_psychological: false,
                    andragogical: false,
                    other: false,
                    other_text: '',
                })),
                strategic_decision_making: [],
                effective_work_form: null,
                staff_training_needs_researched: false,
                qualification_improvement_initiative: [],
                qualification_improvement_planned: false,
                qualification_improvement_encouragement: [],
                competence_portfolios_prepared: [],
                image_building_activities: [],
                specialists_invited_for_image: false,
                specialists_invited_description: '',
            });
        }
    }, [leadershipManagement, form]);

    const handleSave = async (values: any) => {
        // Transform qualification improvement matrix
        const matrix = (values.qualification_improvement_matrix || []).map((item: any) => ({
            frequency: item.frequency,
            management: item.management || false,
            pedagogical_psychological: item.pedagogical_psychological || false,
            andragogical: item.andragogical || false,
            other: item.other || false,
            other_text: item.other ? (item.other_text || '') : '',
        }));

        const requestData: any = {
            ...(leadershipManagement?.id && { id: leadershipManagement.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            head_education_level: values.head_education_level || null,
            has_head_management_education: values.has_head_management_education || false,
            head_speciality: values.has_head_management_education ? (values.head_speciality || null) : null,
            has_management_experience: values.has_management_experience || false,
            management_experience_description: values.has_management_experience ? (values.management_experience_description || null) : null,
            qualification_improvement_matrix: matrix.length > 0 ? matrix : null,
            strategic_decision_making: values.strategic_decision_making && values.strategic_decision_making.length > 0 ? values.strategic_decision_making : null,
            effective_work_form: values.effective_work_form || null,
            staff_training_needs_researched: values.staff_training_needs_researched || false,
            qualification_improvement_initiative: values.qualification_improvement_initiative && values.qualification_improvement_initiative.length > 0 ? values.qualification_improvement_initiative : null,
            qualification_improvement_planned: values.qualification_improvement_planned || false,
            qualification_improvement_encouragement: values.qualification_improvement_encouragement && values.qualification_improvement_encouragement.length > 0 ? values.qualification_improvement_encouragement : null,
            qualification_improvement_encouragement_other: qualificationImprovementEncouragementOther || null,
            competence_portfolios_prepared: values.competence_portfolios_prepared && values.competence_portfolios_prepared.length > 0 ? values.competence_portfolios_prepared : null,
            image_building_activities: values.image_building_activities && values.image_building_activities.length > 0 ? values.image_building_activities : null,
            image_building_activities_other: imageBuildingActivitiesOther || null,
            specialists_invited_for_image: values.specialists_invited_for_image || false,
            specialists_invited_description: values.specialists_invited_for_image ? (values.specialists_invited_description || null) : null,
        };

        await dispatch(createOrUpdateLeadershipManagement(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getLeadershipManagement({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    // Qualification improvement matrix columns
    const qualificationMatrixColumns = [
        {
            title: 'Dažnumas',
            dataIndex: 'frequency',
            key: 'frequency',
            width: '30%',
            render: (_value: any, _record: any, index: number) => {
                const freq = qualificationFrequencies[index];
                return <Text>{freq?.title}</Text>;
            },
        },
        {
            title: 'Vadybinę',
            dataIndex: 'management',
            key: 'management',
            width: '17.5%',
            align: 'center' as const,
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['qualification_improvement_matrix', index, 'management']}
                    style={{ margin: 0 }}
                    valuePropName="checked">
                    <Checkbox />
                </Form.Item>
            ),
        },
        {
            title: 'Pedagoginę psichologinę',
            dataIndex: 'pedagogical_psychological',
            key: 'pedagogical_psychological',
            width: '17.5%',
            align: 'center' as const,
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['qualification_improvement_matrix', index, 'pedagogical_psychological']}
                    style={{ margin: 0 }}
                    valuePropName="checked">
                    <Checkbox />
                </Form.Item>
            ),
        },
        {
            title: 'Andragoginę',
            dataIndex: 'andragogical',
            key: 'andragogical',
            width: '17.5%',
            align: 'center' as const,
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['qualification_improvement_matrix', index, 'andragogical']}
                    style={{ margin: 0 }}
                    valuePropName="checked">
                    <Checkbox />
                </Form.Item>
            ),
        },
        {
            title: 'Kitą',
            dataIndex: 'other',
            key: 'other',
            width: '17.5%',
            align: 'center' as const,
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['qualification_improvement_matrix', index, 'other']}
                    style={{ margin: 0 }}
                    valuePropName="checked">
                    <Checkbox />
                </Form.Item>
            ),
        },
    ];

    return (
        <ConfigProvider locale={locale}>
            <div className="school-leadership-management">
                <Card title="Vadovavimas, personalo vadyba ir įvaizdžio kūrimas. DIREKTORĖ" className="leadership-management-form-card" loading={isLeadershipManagementFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {leadershipManagement?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {leadershipManagement.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti vadovavimo ir valdymo duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isLeadershipManagementFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            head_education_level: '',
                            has_head_management_education: false,
                            head_speciality: '',
                            has_management_experience: false,
                            management_experience_description: '',
                            qualification_improvement_matrix: qualificationFrequencies.map(freq => ({
                                frequency: freq.code,
                                management: false,
                                pedagogical_psychological: false,
                                andragogical: false,
                                other: false,
                                other_text: '',
                            })),
                            strategic_decision_making: [],
                            effective_work_form: null,
                            staff_training_needs_researched: false,
                            qualification_improvement_initiative: [],
                            qualification_improvement_planned: false,
                            qualification_improvement_encouragement: [],
                            competence_portfolios_prepared: [],
                            image_building_activities: [],
                            specialists_invited_for_image: false,
                            specialists_invited_description: '',
                        }}>

                        {/* 8.1 Head education level */}
                        <Card title="8.1 Koks yra institucijos vadovo išsilavinimas? (įrašykite)" style={{ marginBottom: 16 }}>
                            <Form.Item name="head_education_level" label="Vadovo išsilavinimas">
                                <TextArea rows={2} placeholder="Įrašykite vadovo išsilavinimą" />
                            </Form.Item>
                        </Card>

                        {/* 8.2 Qualification or scientific degree */}
                        <Card title="8.2 Ar institucijos vadovas turi kvalifikacinį ar mokslinį laipsnį?" style={{ marginBottom: 16 }}>
                            <Form.Item name="has_head_management_education" valuePropName="checked">
                                <Checkbox>Turi kvalifikacinį ar mokslinį laipsnį</Checkbox>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.has_head_management_education !== currentValues.has_head_management_education}>
                                {({ getFieldValue }) => {
                                    const hasDegree = getFieldValue('has_head_management_education');
                                    return hasDegree ? (
                                        <Form.Item name="head_speciality">
                                            <Radio.Group>
                                                <Radio value="magistro">8.2.1 – magistro</Radio>
                                                <Radio value="daktaro">8.2.2 – daktaro</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 8.3 Management experience */}
                        <Card title="8.3 Ar institucijos vadovas turi vadybinės patirties?" style={{ marginBottom: 16 }}>
                            <Form.Item name="has_management_experience">
                                <Radio.Group>
                                    <Radio value>8.3.1 – taip (įrašykite)</Radio>
                                    <Radio value={false}>8.3.2 – ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.has_management_experience !== currentValues.has_management_experience}>
                                {({ getFieldValue }) => {
                                    const hasExperience = getFieldValue('has_management_experience');
                                    return hasExperience ? (
                                        <Form.Item name="management_experience_description">
                                            <Input placeholder="Įrašykite patirtį" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 8.4 Qualification improvement frequency */}
                        <Card title="8.4 Kaip dažnai institucijos vadovas tobulina savo kvalifikaciją? (pažymėkite + atitinkamame langelyje)" style={{ marginBottom: 16 }}>
                            <Form.Item name="qualification_improvement_matrix">
                                <Table
                                    columns={qualificationMatrixColumns}
                                    dataSource={qualificationFrequencies.map((freq, index) => ({ key: index, frequency: freq.code }))}
                                    pagination={false}
                                    bordered
                                    size="small" />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.qualification_improvement_matrix !== currentValues.qualification_improvement_matrix}>
                                {({ getFieldValue }) => {
                                    const matrix = getFieldValue('qualification_improvement_matrix') || [];
                                    return (
                                        <>
                                            {matrix.map((item: any, index: number) => {
                                                if (item?.other) {
                                                    return (
                                                        <Form.Item
                                                            key={`other_text_${item.frequency || index}`}
                                                            name={['qualification_improvement_matrix', index, 'other_text']}
                                                            style={{ marginTop: 8 }}>
                                                            <Input placeholder="Įrašykite" />
                                                        </Form.Item>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Card>

                        {/* 8.5 Strategic decision making */}
                        <Card title="8.5 Kaip institucijoje dažniausiai priimami strateginiai sprendimai?" style={{ marginBottom: 16 }}>
                            <Form.Item name="strategic_decision_making">
                                <Checkbox.Group>
                                    {strategicDecisionMakingOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.6 Effective work form */}
                        <Card title="8.6 Kuri darbo formą Jūsų manymu, yra efektyvesnė institucijoje?" style={{ marginBottom: 16 }}>
                            <Form.Item name="effective_work_form">
                                <Radio.Group>
                                    {effectiveWorkFormOptions.map((option) => (
                                        <Radio key={option.code} value={option.code}>
                                            {option.title}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.7 Staff training needs researched */}
                        <Card title="8.7 Ar institucijoje tiriami personalo mokymosi poreikiai?" style={{ marginBottom: 16 }}>
                            <Form.Item name="staff_training_needs_researched">
                                <Radio.Group>
                                    <Radio value>8.7.1 – taip</Radio>
                                    <Radio value={false}>8.7.2 – ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.8 Qualification improvement initiative */}
                        <Card title="8.8 Kieno iniciatyva institucijos darbuotojai dažniausiai tobulina kvalifikaciją?" style={{ marginBottom: 16 }}>
                            <Form.Item name="qualification_improvement_initiative">
                                <Checkbox.Group>
                                    {qualificationImprovementInitiativeOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.9 Qualification improvement planned */}
                        <Card title="8.9 Ar darbuotojų kvalifikacijos tobulinimas planuojamas ir koordinuojamas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="qualification_improvement_planned">
                                <Radio.Group>
                                    <Radio value>8.9.1 – taip</Radio>
                                    <Radio value={false}>8.9.2 – ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.10 Qualification improvement encouragement */}
                        <Card title="8.10 Kaip skatinamas personalo kvalifikacijos tobulinimas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="qualification_improvement_encouragement">
                                <Checkbox.Group>
                                    {qualificationImprovementEncouragementOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.qualification_improvement_encouragement !== currentValues.qualification_improvement_encouragement}>
                                {({ getFieldValue }) => {
                                    const encouragement = getFieldValue('qualification_improvement_encouragement') || [];
                                    return encouragement.includes('kita') ? (
                                        <Form.Item>
                                            <Input
                                                placeholder="Įrašykite"
                                                value={qualificationImprovementEncouragementOther}
                                                onChange={(e) => setQualificationImprovementEncouragementOther(e.target.value)} />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 8.11 Competence portfolios */}
                        <Card title="8.11 Ar institucijos vadovas ir darbuotojai rengia savo kompetencijos aplankus (portfelius)?" style={{ marginBottom: 16 }}>
                            <Form.Item name="competence_portfolios_prepared">
                                <Checkbox.Group>
                                    {competencePortfoliosOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 8.12 Image building activities */}
                        <Card title="8.12 Kokios specialios veiklos, skirtos organizaciją reprezentuoti klientams, imasi institucija?" style={{ marginBottom: 16 }}>
                            <Form.Item name="image_building_activities">
                                <Checkbox.Group>
                                    {imageBuildingActivitiesOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.image_building_activities !== currentValues.image_building_activities}>
                                {({ getFieldValue }) => {
                                    const activities = getFieldValue('image_building_activities') || [];
                                    return activities.includes('kita') ? (
                                        <Form.Item>
                                            <Input
                                                placeholder="Įrašykite"
                                                value={imageBuildingActivitiesOther}
                                                onChange={(e) => setImageBuildingActivitiesOther(e.target.value)} />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 8.13 Specialists invited for image */}
                        <Card title="8.13 Ar kurti institucijos įvaizdį kviečiami specialistai?" style={{ marginBottom: 16 }}>
                            <Form.Item name="specialists_invited_for_image">
                                <Radio.Group>
                                    <Radio value>8.13.1 – taip (įrašykite)</Radio>
                                    <Radio value={false}>8.13.2 – ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.specialists_invited_for_image !== currentValues.specialists_invited_for_image}>
                                {({ getFieldValue }) => {
                                    const invited = getFieldValue('specialists_invited_for_image');
                                    return invited ? (
                                        <Form.Item name="specialists_invited_description">
                                            <Input placeholder="Įrašykite" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScLeadershipManagement;
